import { useRef, useState } from "react";
import { User } from "lucide-react";
import { validateImageFile, createImagePreview } from "@/utils/imageProcessing";
import { useToast } from "@/hooks/useToast";

export interface PhotoUploadProps {
  /** Existing image URL (or data URL preview) to show inside the circle */
  currentImageUrl?: string;
  /** Called with the chosen File and its generated preview data URL */
  onImageSelect: (file: File, previewUrl: string) => void;
  /** Diameter of the avatar circle in px */
  size?: number;
  /** Helper text shown beside the button */
  hint?: string;
  buttonLabel?: string;
}

/**
 * Static avatar circle + a separate "Upload Photo" button laid out side by
 * side (the signup "Profile Photo" pattern). Distinct from ProfileImageUpload,
 * which is a hover-to-edit avatar. Reuses the shared image utils + Button.
 */
export const PhotoUpload = ({
  currentImageUrl,
  onImageSelect,
  size = 64,
  hint,
  buttonLabel = "Upload Photo",
}: PhotoUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { showToast } = useToast();

  const displayImage = previewUrl || currentImageUrl;

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      validateImageFile(file);
      const preview = await createImagePreview(file);
      setPreviewUrl(preview);
      onImageSelect(file, preview);
    } catch (err) {
      showToast({
        type: "error",
        message: err instanceof Error ? err.message : "Please choose a valid image file.",
      });
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div
        className="rounded-full bg-theme-bg-surface-raised border border-theme-border flex items-center justify-center overflow-hidden shrink-0"
        style={{ width: size, height: size }}
      >
        {displayImage ? (
          <img src={displayImage} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <User className="text-theme-text-muted" size={size * 0.5} strokeWidth={1.5} />
        )}
      </div>
      <div className="flex flex-col gap-1.5 items-start">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="h-[42px] px-[17px] inline-flex items-center rounded-[14px] border border-theme-border text-14 font-medium text-theme-text-secondary hover:bg-theme-bg-hover transition-colors"
        >
          {buttonLabel}
        </button>
        {hint && <p className="text-12 text-theme-text-muted">{hint}</p>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
};
